import { FormatQuoteRounded, ForumOutlined } from "@mui/icons-material";
import { Box, Grid, Typography } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import copy from "copy-to-clipboard";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import React from "react";
import { useNavigate } from "react-router";
import { useStore } from "utils/useStore";
import { ActionTypeToComponent } from "../Actions/Actions";
import { AgentAvatar } from "../../Branding/AgentAvatar";
import Message from "../Messages/Message";
import { AgentState } from "../../Models/AgentState";
import { MessageAuthor } from "../../Models/MessageAuthor";
import { IChatMessageGroup } from "../../Models/ChatMessageGroup";
import type { ChatSuggestion } from "../../Models/ChatSuggestion";
import { Suggestion } from "../Suggestions/Suggestion";
import { SuggestionsList } from "../Suggestions/SuggestionsList";
import { MessageGroupActions } from "./MessageGroupActions";
import { useCurrentChatStore } from "components/KBo/Stores/useCurrentChat";
import { marked } from "marked";
import { ChatActionType } from "../../Models/ChatAction";
import type { KboFlowResearchPreview } from "services/AcxAgentService";
import AgentStore from "components/KBo/Stores/AgentStore";
import { AgentArtifacts } from "./AgentArtifacts";

// Players rendered under the message, in this order, rather than with the suggestions
const InlineActionTypes: ChatActionType[] = [
    ChatActionType.ShowSoundClips,
    ChatActionType.PlayMontage,
];

// Icons for KBo's research finding actions.
const suggestionIcons: Record<string, React.ReactNode> = {
    "View evidence": <FormatQuoteRounded fontSize="small" />,
    "View conversations": <ForumOutlined fontSize="small" />,
};

type ChatMessageGroupProps = {
    isMostRecent: boolean;
    cancelledStreaming: boolean;
    maxMessageWidth: string;
    fullscreen?: boolean;
    kboFlowPreview?: KboFlowResearchPreview;
    onOpenFullscreen?: () => void;
} & IChatMessageGroup;

export const MessageGroup = observer(
    ({
        author,
        transientMessage,
        suggestions,
        actions,
        functionCalls,
        artifacts,
        userFeedbackType,
        userFeedback,
        isMostRecent,
        cancelledStreaming,
        maxMessageWidth,
        fullscreen,
        kboFlowPreview,
        onOpenFullscreen,
        ...props
    }: React.ComponentPropsWithoutRef<typeof Grid> & ChatMessageGroupProps) => {
        const currentChatStore = useCurrentChatStore();
        const messageStore = useStore(MessageStore);
        // Suggestions, actions and feedback stay off until the KBo User Agreement is accepted.
        const isLocked = useStore(AgentStore).isKboInteractionLocked;
        const navigate = useNavigate();

        const isUserMessage = author === MessageAuthor.User;
        const isAgentMessage = author === MessageAuthor.Agent;
        const hasHtmlDocument = props.messages.some(
            (message) => message.renderMode === "htmlDocument",
        );
        const isFullWidthReport = fullscreen && hasHtmlDocument;
        // A group can hold suggestions only (e.g. the Analyze Trends choices);
        // don't render an empty avatar row for it.
        const showMessages =
            props.messages.length > 0 || (isMostRecent && !!transientMessage);
        const hasDebugAction = actions?.has(ChatActionType.ViewDebug) ?? false;
        const debugActionArgs = hasDebugAction
            ? actions?.get(ChatActionType.ViewDebug)
            : undefined;

        // Older groups only keep their permanent suggestions.
        const visibleSuggestions = isMostRecent
            ? suggestions
            : suggestions?.filter((suggestion) => suggestion.permanent);

        async function handleSuggestionClick(suggestion: ChatSuggestion) {
            // Navigation-only suggestions skip the store so they don't cancel a response.
            if (suggestion.action || suggestion.serverAction) {
                const didRun = await currentChatStore?.handleSuggestion(
                    suggestion,
                );
                if (!didRun) return;
            }
            if (suggestion.navigationURL) {
                // Suggestions are observable; unwrap the state so history can clone it.
                navigate(suggestion.navigationURL, {
                    state: toJS(suggestion.navigationState),
                });
            }
        }

        async function copyToClipboard() {
            let plainTextContent = props.messages
                .map((message) => message.toString())
                .join("\n");

            if (transientMessage) {
                plainTextContent += `\n${transientMessage.toString()}`;
            }

            const htmlContent = await marked(plainTextContent);

            try {
                if (navigator.clipboard && window.ClipboardItem) {
                    const blob = new Blob([htmlContent], { type: "text/html" });
                    const plainTextBlob = new Blob([plainTextContent], {
                        type: "text/plain",
                    });
                    const clipboardItem = new ClipboardItem({
                        "text/html": blob,
                        "text/plain": plainTextBlob,
                    });
                    await navigator.clipboard.write([clipboardItem]);
                } else {
                    copy(plainTextContent);
                }
                messageStore.logInfo("Content copied to clipboard");
            } catch (error) {
                copy(plainTextContent);
                messageStore.logInfo("Content copied to clipboard");
            }
        }

        return (
            <Grid
                container
                item
                direction="column"
                flexWrap="nowrap"
                gap={1}
                alignSelf={isUserMessage ? "self-end" : "self-start"}
                width={isFullWidthReport ? "100%" : maxMessageWidth}
                {...props}
            >
                {showMessages && (
                    <Grid
                        container
                        item
                        alignItems="flex-end"
                        flexWrap="nowrap"
                        gap={1}
                        width={isFullWidthReport ? "100%" : undefined}
                    >
                        {isAgentMessage && !isFullWidthReport && (
                            <Grid item>
                                <AgentAvatar />
                            </Grid>
                        )}
                        <Grid
                            container
                            item
                            direction="column"
                            rowGap={1}
                            flexWrap="nowrap"
                            alignItems={isUserMessage ? "flex-end" : "flex-start"}
                            width={isFullWidthReport ? "100%" : undefined}
                        >
                            {props.messages.map((message) => (
                                <Message
                                    key={message.id}
                                    author={author}
                                    fullscreen={fullscreen}
                                    kboFlowPreview={kboFlowPreview}
                                    onOpenFullscreen={onOpenFullscreen}
                                    {...message}
                                />
                            ))}
                            {isMostRecent && transientMessage && (
                                <Message
                                    key={transientMessage.id}
                                    author={author}
                                    fullscreen={fullscreen}
                                    kboFlowPreview={kboFlowPreview}
                                    onOpenFullscreen={onOpenFullscreen}
                                    {...transientMessage}
                                />
                            )}
                            {isAgentMessage && artifacts && (
                                <AgentArtifacts artifacts={artifacts} />
                            )}
                        </Grid>
                    </Grid>
                )}
                {/* Shown outside the suggestions below so clips and montages stay playable after later messages */}
                {actions &&
                    InlineActionTypes.filter((type) => actions.has(type)).map(
                        (type) => {
                            const Component = ActionTypeToComponent[type];
                            return (
                                <Grid
                                    key={type}
                                    container
                                    item
                                    flexWrap="nowrap"
                                    gap={1}
                                >
                                    <Grid
                                        item
                                        width="28px"
                                        flexShrink={0}
                                    ></Grid>
                                    {/* Disabled while KBo is locked, like the action widgets below */}
                                    <Box
                                        component="fieldset"
                                        disabled={isLocked}
                                        sx={{
                                            border: 0,
                                            padding: 0,
                                            margin: 0,
                                            minWidth: 0,
                                            flexGrow: 1,
                                        }}
                                    >
                                        <Component
                                            args={actions.get(type)}
                                            remove={() => actions.delete(type)}
                                        />
                                    </Box>
                                </Grid>
                            );
                        },
                    )}
                {cancelledStreaming && (
                    <Grid
                        className={"pendo-ignore"}
                        container
                        item
                        alignItems="flex-end"
                        flexWrap="nowrap"
                        gap={1}
                        direction={"row"}
                    >
                        <Grid item width="28px"></Grid>
                        <Grid item>
                            <Typography
                                sx={(theme) => ({
                                    color: theme.palette.gray[600],
                                    fontSize: "13px",
                                    fontStyle: "italic",
                                    lineHeight: "20px",
                                    paddingLeft: theme.spacing(2),
                                })}
                            >
                                You cancelled the response
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                {!cancelledStreaming &&
                    !isLocked &&
                    isMostRecent &&
                    !transientMessage &&
                    isAgentMessage &&
                    props.messages.length > 0 &&
                    currentChatStore?.state === AgentState.Idle && (
                        <MessageGroupActions
                            key={props.messages.at(-1)?.id}
                            onCopy={copyToClipboard}
                            functionCalls={functionCalls}
                            hasDebugAction={hasDebugAction}
                            debugActionArgs={debugActionArgs}
                        />
                    )}
                {visibleSuggestions &&
                    (isMostRecent || visibleSuggestions.length > 0) && (
                        <Grid
                            className={"pendo-ignore"}
                            container
                            item
                            alignItems="flex-end"
                            flexWrap="nowrap"
                            gap={1}
                            direction={isAgentMessage ? "row" : "row-reverse"}
                        >
                            {isAgentMessage && <Grid item width="28px"></Grid>}
                            {/* A disabled fieldset turns off every button and input in the action widgets too. */}
                            <Box
                                component="fieldset"
                                disabled={isLocked}
                                sx={{
                                    border: 0,
                                    padding: 0,
                                    margin: 0,
                                    minWidth: 0,
                                    flexGrow: 1,
                                }}
                            >
                                <Grid
                                    container
                                    item
                                    gap={1}
                                    alignItems="flex-start"
                                    justifyContent={
                                        isUserMessage ? "flex-end" : "flex-start"
                                    }
                                >
                                    {visibleSuggestions.map((suggestion) => (
                                        <Grid item key={suggestion.content}>
                                            {suggestion.children ? (
                                                <SuggestionsList
                                                    title={suggestion.content}
                                                >
                                                    {suggestion.children.map(
                                                        (child) => (
                                                            <SuggestionsList.ListItem
                                                                key={
                                                                    child.content
                                                                }
                                                                disabled={
                                                                    isLocked ||
                                                                    child.disabled
                                                                }
                                                                onClick={() =>
                                                                    handleSuggestionClick(
                                                                        child,
                                                                    )
                                                                }
                                                            >
                                                                {child.content}
                                                            </SuggestionsList.ListItem>
                                                        ),
                                                    )}
                                                </SuggestionsList>
                                            ) : (
                                                <Suggestion
                                                    disabled={
                                                        isLocked ||
                                                        suggestion.disabled
                                                    }
                                                    startIcon={
                                                        suggestionIcons[
                                                            suggestion.content
                                                        ]
                                                    }
                                                    onClick={() =>
                                                        handleSuggestionClick(
                                                            suggestion,
                                                        )
                                                    }
                                                >
                                                    {suggestion.content}
                                                </Suggestion>
                                            )}
                                        </Grid>
                                    ))}
                                    {actions &&
                                        actions
                                            .entries()
                                            .map(([type, args]) => {
                                                if (
                                                    type ===
                                                        ChatActionType.ViewDebug ||
                                                    InlineActionTypes.includes(
                                                        type,
                                                    )
                                                ) {
                                                    return null;
                                                }

                                                const Component =
                                                    ActionTypeToComponent[type];
                                                if (!Component) return null;
                                                return (
                                                    <Component
                                                        args={args}
                                                        remove={() =>
                                                            actions.delete(type)
                                                        }
                                                    />
                                                );
                                            })}
                                </Grid>
                            </Box>
                        </Grid>
                    )}
            </Grid>
        );
    },
);
